import PropTypes from "prop-types";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { contentApi } from "src/pages/website-content/api";
import { contrast, imageUrl, isFile, isOn, matches, placeholderWarnings, rights, shortName, toHex } from "src/pages/website-content/checks";
import ResourceList from "src/components/shared/ResourceList";
import {
  CheckCell,
  ConfirmDialog,
  ContentPage,
  EditorDialog,
  Field,
  FieldError,
  FieldLabel,
  ImageField,
  ImagePreview,
  SaveSwitch,
  ShowFilter,
  useConfirm,
  useContentList,
  useForm,
} from "src/pages/website-content/kit";

// Feature cards: the coloured boxes with an icon, a title and a few lines,
// on the Home, About and Safrati AI pages. `type` says which page.

const PLACES = [
  { type: "home", label: "Home cards", was: "the old Home page" },
  { type: "about_us", label: "About cards", was: "the old About page" },
  { type: "ai", label: "Safrati AI cards", was: "the old Safrati AI page" },
];
const placeOf = (type) => PLACES.find((p) => p.type === type);

const EMPTY = { id: null, type: "home", title: "", description: "", icon: "", bg_color: "#FFFFFF", text_color: "#180D6F" };
const LONG = 220;

function readability(bg, text) {
  const ratio = contrast(bg, text);
  if (ratio == null) return null;
  return ratio;
}

// A small square in the card's colours with its icon, to tell cards apart.
function Swatch({ item }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: item.bg_color || "#FFFFFF",
        border: 1,
        borderColor: "divider",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      <ImagePreview src={imageUrl(item.icon)} width={24} height={24} background="transparent" rounded={4} border={false} />
    </Box>
  );
}

Swatch.propTypes = { item: PropTypes.object.isRequired };

// The card as the website draws it.
function CardPreview({ item, width = 260 }) {
  return (
    <Box
      sx={{
        width: { xs: "100%", sm: width },
        p: 2,
        borderRadius: "12px",
        bgcolor: item.bg_color || "#FFFFFF",
        color: item.text_color || "#000000",
        border: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <ImagePreview
        src={isFile(item.icon) ? item.iconUrl : imageUrl(item.icon)}
        width={32}
        height={32}
        background="transparent"
        rounded={4}
        border={false}
      />
      <Typography sx={{ mt: 1, fontWeight: 700, color: "inherit", overflowWrap: "anywhere" }}>{item.title || "Title"}</Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 0.5,
          color: "inherit",
          overflowWrap: "anywhere",
        }}
      >
        {item.description || "A few words about this feature."}
      </Typography>
    </Box>
  );
}

CardPreview.propTypes = { item: PropTypes.object.isRequired, width: PropTypes.number };

// A colour: the native picker plus the value as text (the website also takes
// "rgb(…)" and names like "white").
function ColorField({ label, value, onChange, error, hint }) {
  const id = useId();
  const hex = toHex(value);
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <FieldLabel id={`${id}-label`}>{label}</FieldLabel>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          component="input"
          type="color"
          value={hex || "#ffffff"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          aria-label={`${label}: pick a colour`}
          sx={{
            width: 48,
            height: 48,
            p: 0.5,
            border: 1,
            borderColor: "divider",
            borderRadius: "14px",
            bgcolor: "background.paper",
            cursor: "pointer",
            flexShrink: 0,
          }}
        />
        <TextField
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          inputProps={{ "aria-label": `${label} as a colour code`, "aria-invalid": error ? "true" : "false" }}
          sx={{ "& .MuiInputBase-root": { minHeight: 48, borderRadius: "14px", fontFamily: "monospace" } }}
        />
      </Stack>
      {error ? (
        <FieldError>{error}</FieldError>
      ) : (
        hint && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {hint}
          </Typography>
        )
      )}
    </Box>
  );
}

ColorField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  hint: PropTypes.node,
};

const Services = () => {
  const dispatch = useDispatch();
  const can = rights("14");
  const load = useCallback(() => contentApi.services.list(dispatch), [dispatch]);
  const list = useContentList(load);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [editing, setEditing] = useState(null);
  const form = useForm(EMPTY);
  const confirm = useConfirm();
  const [iconUrl, setIconUrl] = useState("");
  const newIcon = form.values.icon;
  useEffect(() => {
    if (!isFile(newIcon)) {
      setIconUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(newIcon);
    setIconUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [newIcon]);

  const items = useMemo(() => list.items || [], [list.items]);
  const counts = { all: items.length, live: items.filter((i) => isOn(i.is_active)).length };
  counts.hidden = counts.all - counts.live;
  const shown = items
    .filter((i) => (tab === "all" ? true : tab === "hidden" ? !isOn(i.is_active) : i.type === tab))
    .filter((i) => matches(i, search, ["title", "description"]));

  const warningsFor = (item) => {
    const found = placeholderWarnings(item.title, item.description);
    const ratio = readability(item.bg_color, item.text_color);
    if (ratio != null && ratio < 4.5) found.push(`The text is hard to read on this card colour (contrast ${ratio.toFixed(1)}:1, needs 4.5:1).`);
    if (String(item.description || "").length > LONG)
      found.push(`The text is ${item.description.length} characters, so this card is taller than the others. Aim for under ${LONG}.`);
    if (!item.icon) found.push("No icon.");
    return found;
  };

  const setActive = async (item, next) => {
    try {
      await contentApi.services.set(item.id, "is_active", next ? 1 : 0, dispatch);
      list.patch(item.id, { is_active: next ? 1 : 0 });
      toast.success(`“${item.title || "The card"}” is switched ${next ? "on" : "off"}.`);
    } catch {
      /* PostFunction said what went wrong; the switch stays */
    }
  };

  const openEditor = (item) => {
    form.reset(
      item
        ? {
            id: item.id,
            type: item.type || "home",
            title: item.title || "",
            description: item.description || "",
            icon: item.icon || "",
            bg_color: item.bg_color || "",
            text_color: item.text_color || "",
          }
        : EMPTY
    );
    setEditing(item ? "edit" : "add");
  };

  const save = async () => {
    const v = form.values;
    const errors = {};
    if (!placeOf(v.type)) errors.type = "Choose a group.";
    if (!v.title.trim()) errors.title = "Give the card a short title.";
    if (!v.description.trim()) errors.description = "Write a sentence or two about this feature.";
    if (!v.icon) errors.icon = "Choose an icon.";
    if (!toHex(v.bg_color)) errors.bg_color = "Pick a colour, or type one like #FFFFFF.";
    if (!toHex(v.text_color)) errors.text_color = "Pick a colour, or type one like #180D6F.";
    form.setErrors(errors);
    if (Object.keys(errors).length) return false;
    try {
      const body = {
        type: v.type,
        title: v.title.trim(),
        description: v.description.trim(),
        bg_color: v.bg_color.trim(),
        text_color: v.text_color.trim(),
        ...(isFile(v.icon) ? { icon: v.icon } : {}),
      };
      if (editing === "edit") {
        await contentApi.services.edit({ id: v.id, ...body }, dispatch);
        toast.success("Card saved.");
      } else {
        await contentApi.services.add(body, dispatch);
        toast.success(`Card added to the ${placeOf(v.type).label}.`);
      }
      setEditing(null);
      list.reload();
      return true;
    } catch {
      return undefined;
    }
  };

  const draft = { ...form.values, iconUrl };
  const draftRatio = readability(form.values.bg_color, form.values.text_color);

  return (
    <ContentPage
      title="Services"
      intro="Feature cards: an icon, a title and a few lines about what Safrati does."
      where="The website doesn’t show these cards yet: they belonged to the old Home, About and Safrati AI pages."
      canAdd={can.write}
      addLabel="Add a card"
      onAdd={() => openEditor(null)}
      dialogs={
        <>
          <EditorDialog
            open={!!editing}
            title={editing === "edit" ? "Edit card" : "Add a card"}
            subtitle="No page of the current website shows feature cards. Your changes are kept for when one does."
            onClose={() => setEditing(null)}
            onSave={save}
            saveLabel={editing === "edit" ? "Save card" : "Add card"}
            errors={form.errors}
            dirty={form.dirty}
          >
            <Box>
              <FieldLabel id="service-page">Group</FieldLabel>
              <TextField
                select
                fullWidth
                value={form.values.type}
                onChange={(e) => form.set("type", e.target.value)}
                error={!!form.errors.type}
                SelectProps={{ labelId: "service-page" }}
                sx={{ "& .MuiInputBase-root": { minHeight: 48, borderRadius: "14px" } }}
              >
                {PLACES.map((p) => (
                  <MenuItem key={p.type} value={p.type}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
              <FieldError>{form.errors.type}</FieldError>
            </Box>
            <Field label="Title" value={form.values.title} onChange={(v) => form.set("title", v)} error={form.errors.title} counter={40} />
            <Field
              label="Text"
              value={form.values.description}
              onChange={(v) => form.set("description", v)}
              error={form.errors.description}
              hint="One or two sentences. Only say what Safrati really does."
              multiline
              rows={3}
              counter={LONG}
            />
            <ImageField
              label="Icon"
              value={form.values.icon}
              onChange={(v) => form.set("icon", v)}
              error={form.errors.icon}
              canRemove={false}
              hint="A simple SVG or PNG with a transparent background. Shown at 32 × 32 px."
              preview={(src) => <ImagePreview src={src} width={64} height={64} background={form.values.bg_color} />}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <ColorField label="Card colour" value={form.values.bg_color} onChange={(v) => form.set("bg_color", v)} error={form.errors.bg_color} />
              <ColorField
                label="Text colour"
                value={form.values.text_color}
                onChange={(v) => form.set("text_color", v)}
                error={form.errors.text_color}
              />
            </Stack>
            <Box>
              <FieldLabel>Preview</FieldLabel>
              <CardPreview item={draft} width={300} />
              {draftRatio != null && (
                <Typography variant="body2" sx={{ mt: 1 }} color={draftRatio < 4.5 ? "warning.dark" : "text.secondary"} role="status">
                  {draftRatio < 4.5
                    ? `Hard to read: contrast ${draftRatio.toFixed(1)}:1. Choose colours further apart (4.5:1 or more).`
                    : `Easy to read: contrast ${draftRatio.toFixed(1)}:1.`}
                </Typography>
              )}
              {placeholderWarnings(form.values.title, form.values.description).length > 0 && (
                <Typography variant="body2" color="warning.dark" sx={{ mt: 0.5 }}>
                  {placeholderWarnings(form.values.title, form.values.description).join(" ")}
                </Typography>
              )}
            </Box>
          </EditorDialog>

          <ConfirmDialog
            open={confirm.open}
            title={`Delete the card “${confirm.item?.title || ""}”?`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onClose={confirm.close}
            onConfirm={async () => {
              try {
                await contentApi.services.remove(confirm.item.id, dispatch);
                toast.success(`“${confirm.item?.title || "The card"}” deleted.`);
                list.reload();
                return true;
              } catch {
                return false;
              }
            }}
          >
            It comes off the {placeOf(confirm.item?.type)?.label || "cards"}. You can restore it from the Activity log. To keep it, switch it off
            instead.
          </ConfirmDialog>
        </>
      }
    >
      <ResourceList
        label="Feature cards"
        items={shown}
        primaryLabel="Card"
        primary={(item) => ({
          title: item.title,
          subtitle: placeOf(item.type)?.label || "No group",
          avatar: <Swatch item={item} />,
        })}
        columns={[
          { key: "text", label: "Text", hideBelow: "lg", render: (item) => shortName(item.description, 90) },
          {
            key: "active",
            label: "Switched on",
            width: 170,
            render: (item) => (
              <SaveSwitch
                label={isOn(item.is_active) ? "Switched on" : "Switched off"}
                item={item.title}
                checked={isOn(item.is_active)}
                disabled={!can.edit}
                onChange={(next) => setActive(item, next)}
              />
            ),
          },
          { key: "check", label: "Needs a look", hideBelow: "md", render: (item) => <CheckCell warnings={warningsFor(item)} /> },
        ]}
        onOpen={can.edit ? openEditor : undefined}
        openLabel={(item) => `Edit the card “${item.title}” (${placeOf(item.type)?.label || "no group"})`}
        rowActions={(item) => [
          can.edit && { label: "Edit", onClick: () => openEditor(item) },
          can.delete && { label: "Delete", destructive: true, onClick: () => confirm.ask(item) },
        ]}
        search={{ value: search, onChange: setSearch, placeholder: "Search titles and text" }}
        filters={
          <ShowFilter
            value={tab}
            onChange={setTab}
            options={[
              { value: "all", label: "All cards", count: counts.all },
              ...PLACES.map((p) => ({ value: p.type, label: p.label, count: items.filter((i) => i.type === p.type).length })),
              { value: "hidden", label: "Switched off", count: counts.hidden },
            ]}
          />
        }
        loading={list.loading}
        error={list.failed}
        onRetry={list.reload}
        empty={{ title: "No cards yet." }}
      />
    </ContentPage>
  );
};

export default Services;
